import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * URL and Session storage integration for filter persistence
 *
 * This context automatically saves and restores:
 * - Active filter selections (checkboxes, radio buttons, dropdowns)
 * - Current sort selection (price high/low, new launch)
 *
 * Data is stored in both URL query parameters and sessionStorage:
 * - URL parameters for sharing and bookmarking (e.g., sort=price_low_to_high&benefits=Medical,Adventure)
 * - Session storage as fallback for complex filter states
 *
 * Data is automatically restored when:
 * - User refreshes the page
 * - User navigates back to the page
 * - Component remounts
 * - User shares/bookmarks the URL
 *
 * Session storage is cleared when:
 * - Browser tab is closed
 * - clearStoredFilters() is called manually
 */

// Session storage keys
const SESSION_STORAGE_KEYS = {
  ACTIVE_FILTERS: "mypartner_active_filters",
  CURRENT_SORT: "mypartner_current_sort",
} as const;

export const SEGMENT_FILTER_KEY = "segment";
export const SEGMENT_NET_PRICE = "NET_PRICE";
export const SEGMENT_REGULAR = "REGULAR";
export const PRICING_VIEW_FILTER_KEY = "price_view";

export const PRICING_VIEW_KEY = "pricingView";
export const PRICING_VIEW_NET = "net_pricing";
export const PRICING_VIEW_REGULAR = "regular";
export const PRICING_VIEW_CHANGE_EVENT = "pricingviewchange";

export const isPricingViewFilterKey = (
  filterKey: string | null | undefined
): boolean => {
  if (!filterKey?.trim()) return false;
  const normalized = filterKey.trim().toLowerCase();
  return (
    normalized === SEGMENT_FILTER_KEY || normalized === PRICING_VIEW_FILTER_KEY
  );
};

const DISPLAY_ONLY_FILTER_KEYS = new Set([
  SEGMENT_FILTER_KEY,
  PRICING_VIEW_FILTER_KEY,
  PRICING_VIEW_KEY,
]);

export const normalizeSegmentKey = (
  segmentKey: string | null | undefined
): string | null => {
  if (!segmentKey?.trim()) return null;
  return segmentKey.trim().toUpperCase();
};

const isLegacyPricingSegmentParam = (
  segment: string | null | undefined
): boolean => {
  const normalized = normalizeSegmentKey(segment);
  return normalized === SEGMENT_NET_PRICE || normalized === SEGMENT_REGULAR;
};

export const segmentKeysMatch = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => {
  const normalizedA = normalizeSegmentKey(a);
  const normalizedB = normalizeSegmentKey(b);
  if (!normalizedA || !normalizedB) return false;
  return normalizedA === normalizedB;
};

export const isNetPricingSegment = (
  segment: string | null | undefined
): boolean => normalizeSegmentKey(segment) === SEGMENT_NET_PRICE;

export const isNetPricingView = (
  pricingView: string | null | undefined
): boolean => {
  if (!pricingView?.trim()) return false;
  const normalized = pricingView.trim().toLowerCase();
  return normalized === PRICING_VIEW_NET || normalized === "net_price";
};

export const segmentKeyToPricingView = (
  segmentKey: string | null | undefined
): string | null => {
  const normalized = normalizeSegmentKey(segmentKey);
  if (!normalized) return null;
  if (normalized === SEGMENT_NET_PRICE) return PRICING_VIEW_NET;
  if (normalized === SEGMENT_REGULAR) return PRICING_VIEW_REGULAR;
  return null;
};

export const pricingViewToSegmentKey = (
  pricingView: string | null | undefined
): string | null => {
  if (!pricingView?.trim()) return null;
  const normalized = pricingView.trim().toLowerCase();
  if (normalized === PRICING_VIEW_NET || normalized === "net_price") {
    return SEGMENT_NET_PRICE;
  }
  if (normalized === PRICING_VIEW_REGULAR || normalized === "regular") {
    return SEGMENT_REGULAR;
  }
  return null;
};

export const readNetPricingFromUrl = (): boolean => {
  if (typeof window === "undefined") return false;
  const searchParams = new URLSearchParams(window.location.search);
  const pricingView = searchParams.get(PRICING_VIEW_KEY);
  return pricingView ? isNetPricingView(pricingView) : false;
};

const emitPricingViewChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(PRICING_VIEW_CHANGE_EVENT));
};

// Session storage utilities
const saveToSessionStorage = (key: string, value: unknown) => {
  try {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.warn("Failed to save to session storage:", error);
  }
};

const loadFromSessionStorage = <T,>(key: string, defaultValue: T): T => {
  try {
    if (typeof window !== "undefined") {
      const item = sessionStorage.getItem(key);
      if (item) {
        return JSON.parse(item);
      }
    }
  } catch (error) {
    console.warn("Failed to load from session storage:", error);
  }
  return defaultValue;
};

const clearFiltersFromSessionStorage = () => {
  try {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.ACTIVE_FILTERS);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.CURRENT_SORT);
    }
  } catch (error) {
    console.warn("Failed to clear session storage:", error);
  }
};

const updatePricingViewFromSegmentInUrl = (
  segmentKey: string | null | undefined
) => {
  if (typeof window === "undefined") return;
  const pricingView = segmentKeyToPricingView(segmentKey);
  if (!pricingView) return;

  const url = new URL(window.location.href);
  const searchParams = url.searchParams;
  searchParams.set(PRICING_VIEW_KEY, pricingView);

  const newUrl = `${url.pathname}?${searchParams.toString()}`;
  window.history.replaceState({ ...window.history.state }, "", newUrl);
  emitPricingViewChanged();
};

// URL parameter utilities
const updateURLParams = (
  filters: Record<string, boolean>,
  currentSort: string | null
) => {
  if (typeof window === "undefined") return;

  try {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    searchParams.delete("sort");
    const existingSegment = searchParams.get(SEGMENT_FILTER_KEY);
    if (isLegacyPricingSegmentParam(existingSegment)) {
      searchParams.delete(SEGMENT_FILTER_KEY);
    }

    if (currentSort) {
      searchParams.set("sort", currentSort);
    }

    const activeFilters = Object.entries(filters).filter(
      ([_, enabled]) => enabled
    );
    const filterGroups: Record<string, string[]> = {};

    activeFilters.forEach(([filterId]) => {
      const [filterKey, criteriaTitle] = filterId.split("_");
      if (
        filterKey &&
        criteriaTitle &&
        filterKey !== "price" &&
        !DISPLAY_ONLY_FILTER_KEYS.has(filterKey)
      ) {
        if (!filterGroups[filterKey]) {
          filterGroups[filterKey] = [];
        }
        filterGroups[filterKey].push(criteriaTitle);
      }
    });

    const currentFilterKeys = Object.keys(filterGroups);
    const existingFilterKeys: string[] = [];

    searchParams.forEach((value, key) => {
      if (
        key !== "sort" &&
        key !== SEGMENT_FILTER_KEY &&
        key !== PRICING_VIEW_KEY
      ) {
        const isFilterParam = Object.entries(filters).some(([filterId]) =>
          filterId.startsWith(`${key}_`)
        );
        if (isFilterParam) {
          existingFilterKeys.push(key);
        }
      }
    });

    // Remove filter parameters that are no longer active
    existingFilterKeys.forEach((filterKey) => {
      if (!currentFilterKeys.includes(filterKey)) {
        searchParams.delete(filterKey);
      }
    });

    // Add/update active filter groups to URL (comma-separated values)
    Object.entries(filterGroups).forEach(([filterKey, values]) => {
      searchParams.set(filterKey, values.join(","));
    });

    // Update URL without page reload
    const newUrl = `${url.pathname}?${searchParams.toString()}`;
    window.history.replaceState({ ...window.history.state }, "", newUrl);
  } catch (error) {
    console.warn("Failed to update URL parameters:", error);
  }
};

const loadFiltersFromURL = (): {
  activeFilters: Record<string, boolean>;
  currentSort: string | null;
  currentSegment: string | null;
} => {
  if (typeof window === "undefined") {
    return { activeFilters: {}, currentSort: null, currentSegment: null };
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const activeFilters: Record<string, boolean> = {};
    let currentSort: string | null = null;
    let currentSegment: string | null = null;

    const sortParam = searchParams.get("sort");
    if (sortParam) {
      currentSort = sortParam;
    }

    const pricingViewParam = searchParams.get(PRICING_VIEW_KEY);
    const segmentFromPricingView = pricingViewToSegmentKey(pricingViewParam);
    if (segmentFromPricingView) {
      currentSegment = segmentFromPricingView;
    } else {
      const segmentParam = searchParams.get(SEGMENT_FILTER_KEY);
      if (isLegacyPricingSegmentParam(segmentParam)) {
        currentSegment = normalizeSegmentKey(segmentParam);
      }
    }

    searchParams.forEach((value, key) => {
      if (
        key !== "sort" &&
        key !== SEGMENT_FILTER_KEY &&
        key !== PRICING_VIEW_KEY
      ) {
        const values = value.split(",");
        values.forEach((criteriaTitle) => {
          if (criteriaTitle.trim()) {
            const filterId = `${key}_${criteriaTitle.trim()}`;
            activeFilters[filterId] = true;
          }
        });
      }
    });

    return { activeFilters, currentSort, currentSegment };
  } catch (error) {
    console.warn("Failed to load filters from URL:", error);
    return { activeFilters: {}, currentSort: null, currentSegment: null };
  }
};

// Generic sort type - now just a string key from API
export type SortType = string;

export interface FilterCriteria {
  key?: string;
  title: { text: { value: string } };
  quoteIds?: string[];
  defaultSelected?: boolean;
  newIcon?: string;
  infoIcon?: string;
  popover?: {
    description?: {
      text?: { value?: string };
    };
  };
}

export interface FilterData {
  filterId: string;
  title: { text: { value: string } };
  key: string;
  type?: "radio" | "checkbox" | "button";
  criteria: FilterCriteria[];
  enabled?: boolean;
}

export interface FilterContextType {
  // Public state
  quoteIds: string[];

  // Filter management
  setInitialQuoteIds: (quoteIds: string[]) => void;
  setInitialFilterData: (filterList: FilterData[]) => void;

  // Sorting
  currentSort: string | null;
  setSort: (sortKey: string | null) => void;

  // Segment display mode (REGULAR / NET_PRICE) — does not affect quote list
  currentSegment: string | null;
  setSegment: (segmentKey: string) => void;

  // Filtering
  activeFilters: Record<string, boolean>;
  setFilter: (filterId: string, enabled: boolean) => void;

  // Session storage and URL management
  clearStoredFilters: () => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilterContext = (): FilterContextType => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilterContext must be used within a FilterProvider");
  }
  return context;
};

export const useOptionalFilterContext = (): FilterContextType | undefined =>
  useContext(FilterContext);

interface FilterProviderProps {
  children: ReactNode;
}

export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
  // Private state
  const [initialQuoteIds, setInitialQuoteIds] = useState<string[]>([]);
  const [filterData, setFilterData] = useState<FilterData[]>([]);

  // Helper function to find sort criteria by key
  const findSortCriteria = useCallback(
    (sortFilter: FilterData, sortKey: string): FilterCriteria | undefined => {
      // Find criteria by matching the key directly
      return sortFilter.criteria.find((c) => c.key === sortKey);
    },
    []
  );

  // Public state
  const [quoteIds, setQuoteIds] = useState<string[]>([]);
  const [currentSort, setCurrentSort] = useState<string | null>(() => {
    // First try to load from URL, then fallback to session storage
    const urlFilters = loadFiltersFromURL();
    return (
      urlFilters.currentSort ||
      loadFromSessionStorage(SESSION_STORAGE_KEYS.CURRENT_SORT, null)
    );
  });
  const [currentSegment, setCurrentSegment] = useState<string | null>(() => {
    const urlFilters = loadFiltersFromURL();
    return urlFilters.currentSegment;
  });
  const [activeFilters, setActiveFilters] = useState<Record<string, boolean>>(
    () => {
      // First try to load from URL, then fallback to session storage
      const urlFilters = loadFiltersFromURL();
      const sessionFilters = loadFromSessionStorage(
        SESSION_STORAGE_KEYS.ACTIVE_FILTERS,
        {}
      );
      return Object.keys(urlFilters.activeFilters).length > 0
        ? urlFilters.activeFilters
        : sessionFilters;
    }
  );

  // Initialize quote IDs and preserve original order
  const setInitialQuoteIdsHandler = useCallback((quoteIds: string[]) => {
    setInitialQuoteIds(quoteIds);
    setQuoteIds(quoteIds); // Initially show all
  }, []);

  useEffect(() => {
    saveToSessionStorage(SESSION_STORAGE_KEYS.CURRENT_SORT, currentSort);
    updateURLParams(activeFilters, currentSort);
  }, [currentSort, currentSegment, activeFilters]);

  useEffect(() => {
    const syncSegmentFromUrl = () => {
      const { currentSegment: segmentFromUrl } = loadFiltersFromURL();
      setCurrentSegment((prev) =>
        prev === segmentFromUrl ? prev : segmentFromUrl
      );
    };

    window.addEventListener("popstate", syncSegmentFromUrl);
    window.addEventListener(PRICING_VIEW_CHANGE_EVENT, syncSegmentFromUrl);

    return () => {
      window.removeEventListener("popstate", syncSegmentFromUrl);
      window.removeEventListener(PRICING_VIEW_CHANGE_EVENT, syncSegmentFromUrl);
    };
  }, []);

  useEffect(() => {
    saveToSessionStorage(SESSION_STORAGE_KEYS.ACTIVE_FILTERS, activeFilters);
    updateURLParams(activeFilters, currentSort);
  }, [activeFilters, currentSort, currentSegment]);

  // Initialize filter data
  const setInitialFilterData = useCallback(
    (filterList: FilterData[]) => {
      const processedFilters = filterList.map((filter) => ({
        ...filter,
        enabled: filter.enabled ?? false,
        filterId: filter.filterId || filter.key, // Use filterId or fallback to key
      }));

      setFilterData(processedFilters);

      // Check for defaultSelected in price filter and set currentSort if not already set
      // Priority order: 1) URL params, 2) Session storage, 3) defaultSelected from price filter
      const priceFilter = processedFilters.find(
        (filter) => filter.key === "price"
      );
      if (priceFilter && !currentSort) {
        const defaultSortCriteria = priceFilter.criteria.find(
          (criteria) => criteria.defaultSelected
        );
        if (defaultSortCriteria?.key) {
          setCurrentSort(defaultSortCriteria.key);
        }
      }

      const segmentFilter = processedFilters.find((filter) =>
        isPricingViewFilterKey(filter.key)
      );
      if (segmentFilter && !currentSegment) {
        const defaultSegmentCriteria = segmentFilter.criteria.find(
          (criteria) => criteria.defaultSelected
        );
        if (defaultSegmentCriteria?.key) {
          const normalized = normalizeSegmentKey(defaultSegmentCriteria.key);
          setCurrentSegment(normalized);
          updatePricingViewFromSegmentInUrl(normalized);
        }
      }

      const defaultActiveFilters: Record<string, boolean> = {};
      processedFilters.forEach((filter) => {
        if (DISPLAY_ONLY_FILTER_KEYS.has(filter.key)) return;

        filter.criteria.forEach((criteria) => {
          const criteriaId = `${filter.filterId}_${criteria.title.text.value}`;
          defaultActiveFilters[criteriaId] = criteria.defaultSelected || false;
        });
      });

      // Merge with session storage data, but only if session storage has valid filters
      const savedFilters = loadFromSessionStorage(
        SESSION_STORAGE_KEYS.ACTIVE_FILTERS,
        {}
      );

      // Check if saved filters are relevant to current filter structure
      const savedFilterKeys = Object.keys(savedFilters);
      const currentFilterKeys = Object.keys(defaultActiveFilters);
      const hasRelevantSavedFilters = savedFilterKeys.some((key) =>
        currentFilterKeys.includes(key)
      );

      // Use saved filters if they're relevant, otherwise use defaults
      if (hasRelevantSavedFilters) {
        const mergedFilters = { ...defaultActiveFilters, ...savedFilters };
        // Only keep filters that are valid for current filter structure
        const validMergedFilters: Record<string, boolean> = Object.fromEntries(
          Object.entries(mergedFilters).filter(([key]) =>
            currentFilterKeys.includes(key)
          )
        ) as Record<string, boolean>;
        setActiveFilters(validMergedFilters);
      } else {
        setActiveFilters(defaultActiveFilters);
      }
    },
    [currentSort, currentSegment]
  );

  // Calculate filtered and sorted quote IDs
  const calculateFilteredQuoteIds = useCallback(() => {
    let result = [...initialQuoteIds];

    // Apply active filters
    const enabledFilters = Object.entries(activeFilters)
      .filter(([_, enabled]) => enabled)
      .map(([filterId]) => filterId);

    if (enabledFilters.length > 0) {
      // Group filters by filter type
      const filterGroups: Record<string, string[]> = {};

      enabledFilters.forEach((filterId) => {
        const [filterKey] = filterId.split("_");
        if (!filterGroups[filterKey]) {
          filterGroups[filterKey] = [];
        }
        filterGroups[filterKey].push(filterId);
      });

      // Apply each filter group (excluding price filter which is only for sorting)
      Object.entries(filterGroups).forEach(([filterKey, filterIds]) => {
        if (filterKey === "price" || DISPLAY_ONLY_FILTER_KEYS.has(filterKey)) {
          return;
        }

        const filter = filterData.find(
          (f) => f.filterId === filterKey || f.key === filterKey
        );
        if (!filter) return;

        if (filterKey === "benefits") {
          // For benefits: INTERSECTION (AND logic) - plans must have ALL selected benefits
          filterIds.forEach((filterId) => {
            const criteriaTitle = filterId.replace(`${filterKey}_`, "");
            const criteria = filter.criteria.find(
              (c) => c.title.text.value === criteriaTitle
            );
            if (criteria) {
              // Intersect current result with this benefit's quote IDs
              result = result.filter((id) => criteria.quoteIds?.includes(id));
            }
          });
        } else {
          // For other filters: UNION (OR logic) - plans can have ANY of the selected options
          const allowedQuoteIds = new Set<string>();

          filterIds.forEach((filterId) => {
            const criteriaTitle = filterId.replace(`${filterKey}_`, "");
            const criteria = filter.criteria.find(
              (c) => c.title.text.value === criteriaTitle
            );
            if (criteria) {
              criteria.quoteIds?.forEach((id) => allowedQuoteIds.add(id));
            }
          });

          // Intersect with current result
          result = result.filter((id) => allowedQuoteIds.has(id));
        }
      });
    }

    // Dedupe
    result = [...new Set(result)];

    // Apply sorting
    if (currentSort && filterData.length > 0) {
      const sortFilter = filterData.find((f) => f.key === "price");
      if (sortFilter) {
        const sortCriteria = findSortCriteria(sortFilter, currentSort);

        if (sortCriteria) {
          // Apply sort order while maintaining filtered results
          const sortedResult: string[] = [];
          sortCriteria.quoteIds?.forEach((id) => {
            if (result.includes(id)) {
              sortedResult.push(id);
            }
          });
          result = sortedResult;
        }
      }
    } else {
      // No sorting applied, maintain original order
      const originalOrderResult: string[] = [];
      initialQuoteIds.forEach((id) => {
        if (result.includes(id)) {
          originalOrderResult.push(id);
        }
      });
      result = originalOrderResult;
    }

    return result;
  }, [
    initialQuoteIds,
    filterData,
    activeFilters,
    currentSort,
    findSortCriteria,
  ]);

  // Set sorting
  const setSort = useCallback(
    (sortKey: string | null) => {
      setCurrentSort(sortKey);
      const filtered = calculateFilteredQuoteIds();
      setQuoteIds(filtered);
    },
    [calculateFilteredQuoteIds]
  );

  const setSegment = useCallback((segmentKey: string) => {
    const normalized = normalizeSegmentKey(segmentKey);
    if (normalized) {
      setCurrentSegment(normalized);
      updatePricingViewFromSegmentInUrl(normalized);
    }
  }, []);

  // Clear stored filters from both session storage and URL
  const clearStoredFilters = useCallback(() => {
    clearFiltersFromSessionStorage();
    setCurrentSegment(null);
    // Clear only filter-related URL parameters
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      const searchParams = url.searchParams;

      searchParams.delete("sort");

      const existingSegment = searchParams.get(SEGMENT_FILTER_KEY);
      if (isLegacyPricingSegmentParam(existingSegment)) {
        searchParams.delete(SEGMENT_FILTER_KEY);
      }

      const filterKeysToRemove: string[] = [];
      searchParams.forEach((value, key) => {
        if (
          key !== "sort" &&
          key !== SEGMENT_FILTER_KEY &&
          key !== PRICING_VIEW_KEY
        ) {
          const isFilterParam = Object.entries(activeFilters).some(
            ([filterId]) => filterId.startsWith(`${key}_`)
          );
          if (isFilterParam) {
            filterKeysToRemove.push(key);
          }
        }
      });

      // Remove all identified filter parameters
      filterKeysToRemove.forEach((param) => searchParams.delete(param));

      // Update URL
      const newUrl = `${url.pathname}?${searchParams.toString()}`;
      window.history.replaceState({ ...window.history.state }, "", newUrl);
    }
  }, [activeFilters]);

  // Set individual filter
  const setFilter = useCallback(
    (filterId: string, enabled: boolean) => {
      setActiveFilters((prev) => {
        const updated = { ...prev, [filterId]: enabled };

        // Calculate immediately with the updated state
        const result = [...initialQuoteIds];

        // Apply active filters using the updated state
        const enabledFilters = Object.entries(updated)
          .filter(([_, isEnabled]) => isEnabled)
          .map(([id]) => id);

        if (enabledFilters.length > 0) {
          // Group filters by filter type
          const filterGroups: Record<string, string[]> = {};

          enabledFilters.forEach((filterId) => {
            const [filterKey] = filterId.split("_");
            if (!filterGroups[filterKey]) {
              filterGroups[filterKey] = [];
            }
            filterGroups[filterKey].push(filterId);
          });

          // Apply each filter group (excluding price filter which is only for sorting)
          let filteredResult = result;
          Object.entries(filterGroups).forEach(([filterKey, filterIds]) => {
            // Skip price filter - it's only used for sorting, never for filtering
            if (
              filterKey === "price" ||
              DISPLAY_ONLY_FILTER_KEYS.has(filterKey)
            ) {
              return;
            }

            const filter = filterData.find(
              (f) => f.filterId === filterKey || f.key === filterKey
            );
            if (!filter) return;

            if (filterKey === "benefits") {
              // For benefits: INTERSECTION (AND logic) - plans must have ALL selected benefits
              filterIds.forEach((filterId) => {
                const criteriaTitle = filterId.replace(`${filterKey}_`, "");
                const criteria = filter.criteria.find(
                  (c) => c.title.text.value === criteriaTitle
                );
                if (criteria) {
                  // Intersect current result with this benefit's quote IDs
                  filteredResult = filteredResult.filter((id) =>
                    criteria.quoteIds?.includes(id)
                  );
                }
              });
            } else {
              // For other filters: UNION (OR logic) - plans can have ANY of the selected options
              const allowedQuoteIds = new Set<string>();

              filterIds.forEach((filterId) => {
                const criteriaTitle = filterId.replace(`${filterKey}_`, "");
                const criteria = filter.criteria.find(
                  (c) => c.title.text.value === criteriaTitle
                );
                if (criteria) {
                  criteria.quoteIds?.forEach((id) => allowedQuoteIds.add(id));
                }
              });

              // Intersect with current result
              filteredResult = filteredResult.filter((id) =>
                allowedQuoteIds.has(id)
              );
            }
          });

          // Apply sorting or maintain original order
          let finalResult = [...new Set(filteredResult)]; // Dedupe

          if (currentSort && filterData.length > 0) {
            const sortFilter = filterData.find((f) => f.key === "price");
            if (sortFilter) {
              const sortCriteria = findSortCriteria(sortFilter, currentSort);

              if (sortCriteria) {
                const sortedResult: string[] = [];
                sortCriteria.quoteIds?.forEach((id) => {
                  if (finalResult.includes(id)) {
                    sortedResult.push(id);
                  }
                });
                finalResult = sortedResult;
              }
            }
          } else {
            // No sorting applied, maintain original order
            const originalOrderResult: string[] = [];
            initialQuoteIds.forEach((id) => {
              if (finalResult.includes(id)) {
                originalOrderResult.push(id);
              }
            });
            finalResult = originalOrderResult;
          }

          setQuoteIds(finalResult);
        } else {
          // No filters active, show all in original order
          setQuoteIds([...initialQuoteIds]);
        }

        return updated;
      });
    },
    [initialQuoteIds, filterData, currentSort, findSortCriteria]
  );

  // Recalculate when dependencies change
  React.useEffect(() => {
    if (initialQuoteIds.length > 0 && filterData.length > 0) {
      const filtered = calculateFilteredQuoteIds();
      setQuoteIds(filtered);
    }
  }, [calculateFilteredQuoteIds, initialQuoteIds.length, filterData.length]);

  const value = useMemo(
    () => ({
      quoteIds,
      setInitialQuoteIds: setInitialQuoteIdsHandler,
      setInitialFilterData,
      currentSort,
      setSort,
      currentSegment,
      setSegment,
      activeFilters,
      setFilter,
      clearStoredFilters,
    }),
    [
      quoteIds,
      setInitialQuoteIdsHandler,
      setInitialFilterData,
      currentSort,
      setSort,
      currentSegment,
      setSegment,
      activeFilters,
      setFilter,
      clearStoredFilters,
    ]
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

export default FilterContext;
